import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { WeddingVendor, WeddingCategory } from '../types';


export function useWeddingVendors({ categorySlug, lat, lon, radius, city }: { categorySlug?: string, lat?: number, lon?: number, radius?: number, city?: string } = {}) {
  return useQuery({
    queryKey: ['weddingVendors', categorySlug, lat, lon, radius, city],
    queryFn: async () => {
      let data, error;

      if (lat && lon && radius) {
        // Use RPC for geospatial search
        const result = await supabase.rpc('search_vendors_by_location', {
          lat,
          long: lon,
          radius_km: radius,
          category_slug_input: categorySlug || null
        });
        
        data = result.data;
        error = result.error;

        if (error) throw error;

        if (data && data.length > 0) {
            // The RPC returns `setof wedding_vendors`. It won't have joined `packages` or `category` by default.
            // We need to fetch full details for these vendors.
            const ids = data.map((v: any) => v.id);
            const { data: fullData, error: fullError } = await supabase
                .from('wedding_vendors')
                .select(`
                    *,
                    category:wedding_categories(*),
                    packages:wedding_packages(id, price, images)
                `)
                .in('id', ids);
            
             if (fullError) throw fullError;
             
             // The RPC result is ordered by distance. We should map fullData to respect ids order.
             const sorted = ids.map((id: string) => fullData?.find((v: any) => v.id === id)).filter(Boolean);
             return sorted as (WeddingVendor & { category: WeddingCategory; packages: any[] })[];
        }
        return []; // No data from RPC
        
      } else {
        // Standard Fetch
        let query = supabase
            .from('wedding_vendors')
            .select(`
                *,
                category:wedding_categories(*),
                packages:wedding_packages(id, price, images)
            `)
            .order('rating', { ascending: false }); // Keep original order

        if (categorySlug) {
            // Use inner join to filter by category slug directly
            query = supabase
                .from('wedding_vendors')
                .select(`
                    *,
                    category:wedding_categories!inner(*),
                    packages:wedding_packages(id, price, images)
                `)
                .eq('category.slug', categorySlug)
                .order('rating', { ascending: false }); // Re-apply order after re-selecting
        }
        
        if (city) {
          query = query.ilike('city', `%${city}%`);
        }

        const result = await query;
        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return (data || []) as (WeddingVendor & { category: WeddingCategory; packages: any[] })[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export const useWeddingVendor = (id: string) => {
  return useQuery({
    queryKey: ['weddingVendor', id],
    queryFn: async () => {
      // 1. Fetch Vendor Details
      const { data: vendor, error: vendorError } = await supabase
        .from('wedding_vendors')
        .select(`
          *,
          category:wedding_categories(*)
        `)
        .eq('id', id)
        .single();
      
      if (vendorError) throw vendorError;

      // 2. Fetch Packages separately
      // This avoids issues if the foreign key relationship isn't automatically detecting the join
      const { data: packages, error: packagesError } = await supabase
        .from('wedding_packages')
        .select('*')
        .eq('vendor_id', id);

      if (packagesError) throw packagesError;

      return {
        ...vendor,
        packages: packages || []
      } as WeddingVendor & { category: WeddingCategory; packages: any[] };
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};
